import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse, { HTMLReactParserOptions } from "html-react-parser";
import React from "react";
import { FC } from "react";
import { css } from "styled-components";

interface DivisionModal {
    fields: {
        slideTitle: string;
        description: string;
        optionalButtonLabel: string;
        optionalButtonLink: LinkFieldValue;
    };
}

const enum fields {
    slides = "slides",
    heading = "heading",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.slides]: DivisionModal[];
        [fields.heading]: string;
    };
}

export interface CENLeadCaptureOptionalHeadingInfoCards {
    container?: InjectableCss;
    heading?: InjectableCss;
    childContainer?: InjectableCss;
    buttonLabel?: InjectableCss;
    titleStyle?: InjectableCss;
    descriptionStyle?: InjectableCss;
}

export const infocardsStyles: CENLeadCaptureOptionalHeadingInfoCards = {
    container: {
        css: css`
            display: flex;
            gap: 24px;
            margin-top: 24px;
            padding-bottom: 104px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        flex-direction: column;
                        padding-bottom: 56px;
                    `,
                    css`
                        width: 100%;
                        flex-direction: column;
                        padding-bottom: 56px;
                    `,
                    css`
                        width: 100%;
                        flex-direction: column;
                        padding-bottom: 78px;
                    `,
                    css``,
                    null,
                ])}
        `,
    },
    heading: {
        css: css`
            font-size: ${TYPOGRAPHY.size.largest};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 32px;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    childContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            border: 4px solid #f2f2f2;
            width: 394px;
            padding: 0px 32px 32px 32px;
            height: auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        padding: 0px 28px 32px 32px;
                        gap: 0px;
                        margin-top: 0px;
                    `,
                    css``,
                    css``,
                    css``,
                    null,
                ])}
        `,
    },
    titleStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${getProp("theme.typography.p.color")};
            margin-top: 32px;
        `,
    },
    descriptionStyle: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${getProp("theme.typography.p.color")};
            margin-top: 24px;
            > p {
                font-size: ${TYPOGRAPHY.size.smaller};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css``,
                    css``,
                    css``,
                    null,
                ])}
        `,
    },
    buttonLabel: {
        css: css`
            width: 100%;
            font-size: ${TYPOGRAPHY.size.tiny};
            line-height: 16px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: ${COLORS.link.base};
            cursor: pointer;
            margin-top: 24px;
        `,
    },
};

const styles = infocardsStyles;
export const CENLeadCaptureOptionalHeadingInfoCards: FC<OwnProps> = ({ fields }) => {
    return (
        <>
            <Typography {...styles.heading}>{fields.heading}</Typography>
            <StyledWrapper {...styles.container}>
                {fields.slides?.map(slides => {
                    const { url, title } = useGetLink(slides.fields.optionalButtonLink);
                    const history = useHistory();
                    const { isInShell } = useShellContext();
                    const onClick = () => {
                        if (!url) {
                            return;
                        }

                        if (url.startsWith("http")) {
                            if (isInShell) {
                                return;
                            }

                            window.location.href = url;
                        } else {
                            history.push(url);
                        }
                    };
                    return (
                        <>
                            <StyledWrapper {...styles.childContainer}>
                                <StyledWrapper {...styles.titleStyle}>{slides.fields.slideTitle}</StyledWrapper>
                                <StyledWrapper {...styles.descriptionStyle}>
                                    {parse(transformContentHtml(slides.fields.description), parserOptions)}
                                </StyledWrapper>
                                <Typography {...styles.buttonLabel} onClick={onClick}>
                                    {slides.fields.optionalButtonLabel || title || url}
                                </Typography>
                            </StyledWrapper>
                        </>
                    );
                })}
            </StyledWrapper>
        </>
    );
};
const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: CENLeadCaptureOptionalHeadingInfoCards,
    definition: {
        group: "Basic",
        displayName: "CEN Optional Section Heading InfoCards",
        fieldDefinitions: [
            {
                name: fields.heading,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },

            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },

                    {
                        name: "description",
                        editorTemplate: "RichTextField",
                        displayName: "Description",
                        defaultValue: "",
                    },
                    {
                        name: "optionalButtonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "optionalButtonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
        ],
    },
};
export default widgetModule;
